import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "../App";
import { PageLayout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Calendar, Download, Video, FileText, ShieldCheck, Heart, ArrowLeft, ListChecks, Phone } from "lucide-react";

const WelcomePacket = () => {
  const { programId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/programs/${programId}/welcome-packet`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .finally(() => setLoading(false));
  }, [programId]);

  if (loading) return <PageLayout title="Welcome Packet"><p className="text-slate-500">Loading…</p></PageLayout>;
  if (!data) return <PageLayout title="Welcome Packet"><p className="text-slate-500">Program not found.</p></PageLayout>;

  const { program, welcome_packet: wp = {} } = data;
  const hasContent = wp.pdf_url || wp.video_url || wp.instructor_intro || wp.welcome_letter || (wp.tech_checklist || []).length > 0;

  return (
    <PageLayout
      title={`Welcome to ${program.name}`}
      subtitle={`Everything you need to start strong. ${program.duration_weeks || ""}${program.duration_weeks ? "-week program" : ""}`}
    >
      <div className="max-w-4xl mx-auto space-y-5" data-testid="welcome-packet">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#1E3A8A] hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        {/* Hero */}
        <Card className="overflow-hidden border-0 shadow-xl">
          <div className="bg-gradient-to-r from-[#1E3A8A] to-[#0F1B3D] text-white p-8 md:p-10">
            <p className="text-[#FFD507] text-xs uppercase tracking-[0.2em] font-bold mb-2">Welcome Packet</p>
            <h1 className="text-3xl md:text-4xl font-heading font-bold">{program.title || program.name}</h1>
            {program.description && (
              <p className="text-white/80 mt-3 max-w-2xl">{program.description.slice(0, 240)}…</p>
            )}
            <div className="flex flex-wrap gap-3 mt-5">
              {wp.pdf_url && (
                <a href={wp.pdf_url} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#FFD507] hover:bg-[#FCC100] text-slate-900 font-semibold flex items-center gap-2"><Download className="w-4 h-4" /> Download Packet (PDF)</Button>
                </a>
              )}
              {wp.video_url && (
                <a href={wp.video_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 flex items-center gap-2"><Video className="w-4 h-4" /> Watch Welcome Video</Button>
                </a>
              )}
            </div>
          </div>
        </Card>

        {!hasContent && (
          <Card className="card-surface bg-slate-50">
            <CardContent className="pt-6 pb-6 text-center text-slate-500">
              <Heart className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>The welcome packet for this program is being assembled. Check back soon!</p>
            </CardContent>
          </Card>
        )}

        {wp.welcome_letter && (
          <Card className="card-surface">
            <CardHeader><CardTitle className="text-slate-900 flex items-center gap-2"><Heart className="w-5 h-5 text-[#FFD507]" /> A note from your program lead</CardTitle></CardHeader>
            <CardContent><p className="text-slate-700 whitespace-pre-line">{wp.welcome_letter}</p></CardContent>
          </Card>
        )}

        {wp.instructor_intro && (
          <Card className="card-surface">
            <CardHeader><CardTitle className="text-slate-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#1E3A8A]" /> Meet your instructor</CardTitle></CardHeader>
            <CardContent><p className="text-slate-700 whitespace-pre-line">{wp.instructor_intro}</p></CardContent>
          </Card>
        )}

        {(wp.tech_checklist || []).length > 0 && (
          <Card className="card-surface">
            <CardHeader><CardTitle className="text-slate-900 flex items-center gap-2"><ListChecks className="w-5 h-5 text-emerald-600" /> Tech setup checklist</CardTitle></CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {wp.tech_checklist.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <span className="w-5 h-5 rounded-full border-2 border-slate-300 flex-shrink-0 mt-0.5"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {(wp.key_dates || []).length > 0 && (
          <Card className="card-surface">
            <CardHeader><CardTitle className="text-slate-900 flex items-center gap-2"><Calendar className="w-5 h-5 text-[#1E3A8A]" /> Key dates</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {wp.key_dates.map((d, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase">{d.label}</p>
                    <p className="text-slate-900 font-semibold">{d.date}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {wp.code_of_conduct && (
          <Card className="card-surface">
            <CardHeader><CardTitle className="text-slate-900 flex items-center gap-2"><FileText className="w-5 h-5 text-[#1E3A8A]" /> Code of Conduct</CardTitle></CardHeader>
            <CardContent><p className="text-slate-700 whitespace-pre-line text-sm">{wp.code_of_conduct}</p></CardContent>
          </Card>
        )}

        {(wp.support_contacts || []).length > 0 && (
          <Card className="card-surface">
            <CardHeader><CardTitle className="text-slate-900 flex items-center gap-2"><Phone className="w-5 h-5 text-emerald-600" /> Support & resources</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {wp.support_contacts.map((c, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                    <span className="text-slate-600">{c.label}</span>
                    <span className="text-slate-900 font-medium">{c.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
};

export default WelcomePacket;
